import { encounterField, inventoryField, itemsCol } from '../../shared/array-types';
import { record, upper, type Row, type StatusRow } from '../ability/context';
import { statusRows, statusItemId } from '../statuses/expiry';
import type { GmBoundaryVerdict } from '../statuses/gm-boundary';
import { mountedTrapAbilityIds } from './mounted';

declare global { interface Window {
    ftMakePlacedItemTrapped?: (inventoryId: string, abilityId: string | null) => Promise<boolean>;
} }

export const TRAPPED_ITEM_STATUS = '78C51D2D-5178-551C-1249-B5DDA4083098';
const AREA_TRAP_TYPE = 'B370100A-352C-4C80-A42E-FF0B7EF80DC4';

/** A nested world container is placed; any creature root, unknown parent or cycle is not. */
export function placedTrapItem(host: Row, encounterId: string, lookup: (id: string) => Row | null): boolean {
    const seen = new Set<string>();
    let row: Row | null = host;
    while (row && upper(inventoryField(row, 'inventory_id'))) {
        const id = upper(inventoryField(row, 'inventory_id'));
        if (Number(inventoryField(row, 'inventory_deleted') ?? 0) || seen.has(id)) return false;
        seen.add(id);
        const parent = upper(inventoryField(row, 'possess_id'));
        if (parent && parent === upper(encounterId)) return true;
        row = parent ? lookup(parent) : null;
    }
    return !!row && !record(row.encounter_creatures)
        && upper(encounterField(row, 'encounter_id')) === upper(encounterId) && !!encounterId;
}

export function pointTrapAbility(ability: Row, entries: (row: Row, key: string) => unknown[]): boolean {
    const ids = (key: string) => entries(ability, key).map(entry => upper(typeof entry === 'string' ? entry : itemsCol(entry, 'item_id')));
    return itemsCol(ability, 'item_type') === 'trap_ability' && !ids('trap_type').includes(AREA_TRAP_TYPE)
        && !entries(ability, 'aoe_type').length && !entries(ability, 'persistent_aoe').length;
}

/** The payload survives consumption of the armed row; GM removal retires both explicitly. */
export function placedTrapVerdict(host: Row, ability: Row | null, read: {
    entries(row: Row, key: string): unknown[]; definition(id: string): Row | null; uuid(): string;
}): GmBoundaryVerdict {
    const target = upper(inventoryField(host, 'inventory_id'));
    const verdict: GmBoundaryVerdict = { rows: [], ec: {}, statusesAdded: [], statusesRemoved: [], statusStacks: [], charges: [], playerCharactersChanged: [] };
    const mounted = mountedTrapAbilityIds(host, read.entries, read.definition);
    if (!ability) {
        for (const status of statusRows(host, 'target')) if (mounted.includes(statusItemId(status)) || statusItemId(status) === TRAPPED_ITEM_STATUS)
            verdict.statusesRemoved.push({ status_id: upper(itemsCol(status, 'status_id')), status_item_id: statusItemId(status), target });
        return verdict;
    }
    if (!target || !pointTrapAbility(ability, read.entries)) throw new Error('Choose a point trap ability.');
    if (mounted.length) throw new Error('Remove this item’s existing trap first.');
    const armed = statusRows(host, 'target').some(status => statusItemId(status) === TRAPPED_ITEM_STATUS);
    for (const item of [upper(itemsCol(ability, 'item_id')), ...(armed ? [] : [TRAPPED_ITEM_STATUS])]) {
        const status: StatusRow = { status_id: read.uuid(), status_item_id: item, target, caster: target,
            status_connected_id: read.uuid(), status_stack: 1, ends: '0-0-0-0-0-0',
            status_ends_on_target_start_of_turn: 0, status_ends_on_target_end_of_turn: 0,
            status_ends_on_caster_start_of_next_turn: 0, status_ends_on_caster_end_of_next_turn: 0 };
        verdict.statusesAdded.push(status);
    }
    return verdict;
}
